import {useEffect, useState} from 'react';

import axios from 'axios';
import {useStore} from 'effector-react';

import {getClusterFilters, getClusters} from 'api/map';
import {dashboardState$} from 'models/dashboard';
import {Cluster, ClusterFilterDictionaries, ClustersRequest, MovementFilters} from 'types/geo';

type Viewport = Pick<ClustersRequest, 'south' | 'north' | 'west' | 'east' | 'resolution'>;
export function useMovementMap() {
    const {dateRange} = useStore(dashboardState$);
    const [filters, setFilters] = useState<MovementFilters>({});
    const [viewport, setViewport] = useState<Viewport | null>(null);
    const [dictionaries, setDictionaries] = useState<ClusterFilterDictionaries>({
        eventKinds: [],
        productGroups: [],
    });
    const [clusters, setClusters] = useState<Cluster[]>([]);
    const [selected, setSelected] = useState<Cluster | null>(null);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState(false);
    const [dictionaryError, setDictionaryError] = useState(false);
    useEffect(() => {
        const source = axios.CancelToken.source();
        getClusterFilters(source.token)
            .then((value) => {
                if (!source.token.reason) setDictionaries(value);
            })
            .catch(() => {
                if (!source.token.reason) setDictionaryError(true);
            });
        return () => source.cancel();
    }, []);
    useEffect(() => {
        if (!viewport) return;
        const source = axios.CancelToken.source();
        setPending(true);
        setError(false);
        setSelected(null);
        setClusters([]);
        const timer = setTimeout(() => {
            getClusters(
                {
                    ...filters,
                    ...viewport,
                    eventTimeFrom: dateRange.start.toISOString(),
                    eventTimeTo: dateRange.end.toISOString(),
                },
                source.token,
            )
                .then((value) => {
                    if (!source.token.reason)
                        setClusters(
                            value.filter(
                                (c) =>
                                    Number.isFinite(c.latitude) &&
                                    Math.abs(c.latitude) <= 90 &&
                                    Number.isFinite(c.longitude) &&
                                    Math.abs(c.longitude) <= 180,
                            ),
                        );
                })
                .catch(() => {
                    if (!source.token.reason) setError(true);
                })
                .finally(() => {
                    if (!source.token.reason) setPending(false);
                });
        }, 500);
        return () => {
            clearTimeout(timer);
            source.cancel();
        };
    }, [viewport, filters, dateRange]);
    const onIdle = (map: google.maps.Map) => {
        const bounds = map.getBounds();
        if (!bounds) return;
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        const next = {
            south: Math.max(-90, Math.floor(sw.lat())),
            north: Math.min(90, Math.ceil(ne.lat())),
            west: Math.max(-180, Math.floor(sw.lng())),
            east: Math.min(180, Math.ceil(ne.lng())),
            resolution: Math.max(5, Math.min(11, Math.floor((map.getZoom() || 6) / 2) + 2)),
        };
        setViewport((previous) =>
            JSON.stringify(previous) === JSON.stringify(next) ? previous : next,
        );
    };
    return {
        filters,
        setFilters,
        dictionaries,
        clusters,
        selected,
        setSelected,
        pending,
        error,
        dictionaryError,
        onIdle,
    };
}
